/**
 * Visa requirement checking utility
 * Uses AI knowledge initially - can be enhanced with database later
 */

export type VisaStatus = 'required' | 'not_required' | 'check_required' | 'unknown';

export interface VisaCheckResult {
  status: VisaStatus;
  message: string;
  note?: string;
  processingTime?: string;
  details?: string;
  embassyUrl?: string;
}

/**
 * Check if a visa is likely required for a destination
 * This is a simplified check using heuristics - in production, use a visa database
 * 
 * @param destination - The destination country/city
 * @param citizenship - The traveler's citizenship
 * @param travelingFrom - The origin country (optional)
 * @returns VisaCheckResult with status and message
 */
export function checkVisaRequirement(
  destination?: string,
  citizenship?: string,
  travelingFrom?: string
): VisaCheckResult {
  // If no destination or citizenship, we can't determine
  if (!destination || !citizenship) {
    return {
      status: 'check_required',
      message: 'Visa requirements unknown',
      note: 'Please check visa requirements with the destination country\'s embassy or consulate.',
    };
  }

  // Extract country names (handle city, country format)
  const destinationCountry = extractCountry(destination);
  const citizenshipCountry = extractCountry(citizenship);
  const originCountry = travelingFrom ? extractCountry(travelingFrom) : null;

  // Same country - no visa needed
  if (destinationCountry.toLowerCase() === citizenshipCountry.toLowerCase()) {
    return {
      status: 'not_required',
      message: 'No visa required',
      note: 'You are traveling within your own country.',
    };
  }

  // Check for specific visa requirements in our database
  const visaInfo = getVisaRequirementInfo(citizenshipCountry, destinationCountry);
  if (visaInfo) {
    return visaInfo;
  }

  // For countries not in our database, return a generic check required message
  return {
    status: 'check_required',
    message: 'Visa may be required',
    note: `Please verify visa requirements for ${citizenshipCountry} citizens traveling to ${destinationCountry}. Check with the destination country's embassy or consulate for the most up-to-date information.`,
    embassyUrl: getEmbassyUrl(destinationCountry),
  };
}

/**
 * Extract country name from a string that might contain city, country format
 * @param location - Location string (e.g., "Tokyo, Japan" or "Nigeria")
 * @returns Country name
 */
function extractCountry(location: string): string {
  // If it contains a comma, take the last part (usually the country)
  const parts = location.split(',').map(p => p.trim());
  if (parts.length > 1) {
    return parts[parts.length - 1];
  }
  return location;
}

/**
 * Get visa requirement information for specific country combinations
 */
function getVisaRequirementInfo(citizenship: string, destination: string): VisaCheckResult | null {
  const citizenshipLower = citizenship.toLowerCase();
  const destinationLower = destination.toLowerCase();

  // Nigerian citizens to United States
  if (citizenshipLower.includes('nigeria') && destinationLower.includes('united states')) {
    return {
      status: 'required',
      message: 'Visa required for Nigerian citizens traveling to the United States',
      details: 'Nigerian citizens need a visa to travel to the United States. Processing time typically takes 120 days.',
      processingTime: '120 days',
      note: 'Apply well in advance of your travel date. Processing times may vary.',
      embassyUrl: 'https://www.usembassy.gov/',
    };
  }

  // Nigerian citizens to United Kingdom
  if (citizenshipLower.includes('nigeria') && destinationLower.includes('united kingdom')) {
    return {
      status: 'required',
      message: 'Visa required for Nigerian citizens traveling to the United Kingdom',
      details: 'Nigerian citizens need a visa to travel to the United Kingdom. Processing time typically takes 15-30 working days.',
      processingTime: '15-30 working days',
      note: 'Apply at least 3 months before your travel date.',
      embassyUrl: 'https://www.gov.uk/check-uk-visa',
    };
  }

  // Nigerian citizens to Canada
  if (citizenshipLower.includes('nigeria') && destinationLower.includes('canada')) {
    return {
      status: 'required',
      message: 'Visa required for Nigerian citizens traveling to Canada',
      details: 'Nigerian citizens need a visa to travel to Canada. Processing time typically takes 2-4 weeks.',
      processingTime: '2-4 weeks',
      note: 'Apply through the IRCC website or at a Visa Application Centre.',
      embassyUrl: 'https://www.canada.ca/en/immigration-refugees-citizenship/services/visit-canada.html',
    };
  }

  // Nigerian citizens to Schengen Area (EU)
  if (citizenshipLower.includes('nigeria') && (
    destinationLower.includes('france') || destinationLower.includes('germany') || 
    destinationLower.includes('italy') || destinationLower.includes('spain') ||
    destinationLower.includes('portugal') || destinationLower.includes('netherlands') ||
    destinationLower.includes('belgium') || destinationLower.includes('greece') ||
    destinationLower.includes('switzerland') || destinationLower.includes('austria')
  )) {
    return {
      status: 'required',
      message: 'Visa required for Nigerian citizens traveling to the Schengen Area',
      details: 'Nigerian citizens need a Schengen visa to travel to most European countries. Processing time typically takes 15-30 days.',
      processingTime: '15-30 days',
      note: 'Apply at the embassy or consulate of your main destination country.',
      embassyUrl: 'https://home-affairs.ec.europa.eu/policies/schengen-borders-and-visa/visa-policy_en',
    };
  }

  // US citizens to most countries (visa-free or visa on arrival)
  if (citizenshipLower.includes('united states') || citizenshipLower.includes('usa')) {
    if (destinationLower.includes('united kingdom') || destinationLower.includes('canada') || 
        destinationLower.includes('australia') || destinationLower.includes('new zealand') ||
        destinationLower.includes('japan') || destinationLower.includes('south korea')) {
      return {
        status: 'not_required',
        message: 'No visa required for US citizens',
        note: 'US citizens can travel visa-free to this destination for tourism purposes.',
      };
    }
  }

  // UK citizens to EU/Schengen
  if (citizenshipLower.includes('united kingdom') && (
    destinationLower.includes('france') || destinationLower.includes('germany') || 
    destinationLower.includes('italy') || destinationLower.includes('spain') ||
    destinationLower.includes('portugal')
  )) {
    return {
      status: 'not_required',
      message: 'No visa required for UK citizens',
      note: 'UK citizens can travel visa-free to EU/Schengen countries for short stays (up to 90 days).',
    };
  }

  // Return null if no specific information is available
  return null;
}

/**
 * Get embassy/consulate URL for a destination country
 */
function getEmbassyUrl(country: string): string | undefined {
  const countryLower = country.toLowerCase();
  
  if (countryLower.includes('united states') || countryLower.includes('usa')) {
    return 'https://www.usembassy.gov/';
  }
  if (countryLower.includes('united kingdom')) {
    return 'https://www.gov.uk/world/embassies';
  }
  if (countryLower.includes('canada')) {
    return 'https://www.international.gc.ca/world-monde/index.aspx?lang=eng';
  }
  if (countryLower.includes('australia')) {
    return 'https://www.dfat.gov.au/about-us/our-locations/missions';
  }
  if (countryLower.includes('japan')) {
    return 'https://www.mofa.go.jp/j_info/visit/visa/index.html';
  }
  
  // No Google fallback — return undefined so callers can keep users in-app.
  return undefined;
}

/**
 * Get a user-friendly message for visa status
 */
export function getVisaStatusMessage(status: VisaStatus, destination?: string, citizenship?: string): string {
  switch (status) {
    case 'not_required':
      return `No visa required for ${citizenship || 'your'} citizens traveling to ${destination || 'this destination'}`;
    case 'required':
      return `Visa required for ${citizenship || 'your'} citizens traveling to ${destination || 'this destination'}`;
    case 'check_required':
      return `Please verify visa requirements for ${citizenship || 'your'} citizens traveling to ${destination || 'this destination'}`;
    default:
      return 'Visa requirements unknown';
  }
}

